import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { US_STATES } from "@/lib/usStates";
import { ENTITY_TYPES, defaultEntityType, normalizeEntityType } from "@/lib/entityTypes";
import {
  MapTrifold, CheckCircle, Circle, ArrowSquareOut, CircleNotch,
  Sparkle, Confetti,
} from "@phosphor-icons/react";

const BIZEE_AFFILIATE_URL = "https://www.bizee.com/?ref=ironroot";
const DIY_SECRETARY_OF_STATE_URL = "https://www.llcuniversity.com/50-secretary-of-state-sos-business-entity-search/";
const SETUP_BUSINESS_TYPES = ["General", "Wellness Clinic", "Contractor", "Restaurant", "Real Estate", "E-commerce", "Professional Services"];

function isFormationStep(step, index) {
  const hay = `${step?.title || ""} ${step?.description || ""}`.toLowerCase();
  return index === 0 || ["register your business entity", "form your llc", "secretary of state", "articles of organization"].some((k) => hay.includes(k));
}

export function SetupRoadmap({ businessId, entityType }) {
  const [plan, setPlan] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [state, setState] = useState("");
  const [entity, setEntity] = useState(defaultEntityType(entityType));
  const [bizType, setBizType] = useState("General");

  useEffect(() => {
    api.get(`/businesses/${businessId}/setup`).then((r) => {
      setPlan(r.data.plan);
      setCompleted(r.data.completed || []);
      if (r.data.plan) { setState(r.data.plan.state); setEntity(defaultEntityType(r.data.plan.entity_type)); if (r.data.plan.business_type) setBizType(r.data.plan.business_type); }
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);
  useEffect(() => { setEntity(defaultEntityType(entityType)); }, [entityType]);

  const generate = async () => {
    if (!state) return toast.error("Select your state");
    if (!entity) return toast.error("Select your entity type");
    setGenerating(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/setup/generate`, { state, entity_type: normalizeEntityType(entity), business_type: bizType });
      setPlan(data.plan);
      setCompleted(data.completed || []);
      toast.success("Your personalized setup roadmap is ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate your roadmap.");
    } finally {
      setGenerating(false);
    }
  };

  const toggle = async (stepId, done) => {
    setCompleted((c) => (done ? [...c, stepId] : c.filter((x) => x !== stepId)));
    try {
      const { data } = await api.post(`/businesses/${businessId}/setup/toggle`, { step_id: stepId, done });
      setCompleted(data.completed || []);
      if (done) toast.success("Step done");
    } catch { toast.error("Couldn't save."); }
  };

  if (loading) return <p style={{ color: "var(--bk-text-2)" }}>Loading…</p>;

  const doneSet = new Set(completed);
  const steps = plan?.steps || [];
  const doneCount = steps.filter((s) => doneSet.has(s.id)).length;
  const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;
  const allDone = steps.length > 0 && doneCount === steps.length;

  return (
    <div className="space-y-6" data-testid="setup-roadmap">
      {/* generator */}
      <div className="bk-card p-6">
        <div className="flex items-start gap-3">
          <MapTrifold size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Your setup roadmap</h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
              Pick your state and entity type. AI builds a personalized, correctly-ordered checklist — LLC filing, EIN, tax registration, banking — with official links.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <select value={state} onChange={(e) => setState(e.target.value)} data-testid="setup-state" className="rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                <option value="">Select your state…</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="setup-entity" className="rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                <option value="">Select entity type…</option>
                {ENTITY_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={bizType} onChange={(e) => setBizType(e.target.value)} data-testid="setup-business-type" className="sm:col-span-2 rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                {SETUP_BUSINESS_TYPES.map((s) => <option key={s} value={s}>{`Business type: ${s}`}</option>)}
              </select>
            </div>
            <button onClick={generate} disabled={generating} data-testid="generate-setup-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold mt-4 disabled:opacity-70">
              {generating ? <><CircleNotch size={16} className="animate-spin" /> Building your roadmap…</> : <><Sparkle size={16} weight="fill" /> {plan ? "Regenerate roadmap" : "Generate my roadmap"}</>}
            </button>
          </div>
        </div>
      </div>

      {plan && (
        <>
          {/* progress */}
          <div className="bk-card p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>{[plan.state, plan.entity_type, plan.business_type && plan.business_type !== "General" ? plan.business_type : null].filter(Boolean).join(" · ")}</p>
                <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{doneCount} of {steps.length} steps complete</p>
                {plan.starter && <p className="text-xs mt-1" style={{ color: "var(--bk-accent)" }} data-testid="setup-starter-note">Starter checklist — pick your state above and Generate for a state-specific version.</p>}
              </div>
              <span className="font-display text-2xl font-bold" style={{ color: "var(--bk-accent)" }} data-testid="setup-progress-pct">{pct}%</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--bk-page)" }}>
              <div className="h-2.5 rounded-full" style={{ width: `${pct}%`, background: "var(--bk-success)", transition: "width 0.4s ease" }} />
            </div>
            {allDone && (
              <div className="flex items-center gap-2 mt-4 text-sm font-semibold" style={{ color: "var(--bk-success)" }} data-testid="setup-complete-banner">
                <Confetti size={20} weight="fill" /> You're all set up! Head to Books to start tracking your money.
              </div>
            )}
          </div>

          {/* timeline */}
          <div className="relative pl-8">
            <div className="absolute left-[13px] top-2 bottom-2 w-0.5" style={{ background: "var(--bk-border)" }} />
            <div className="space-y-4">
              {steps.map((s, i) => {
                const done = doneSet.has(s.id);
                return (
                  <div key={s.id} className="relative" data-testid={`setup-step-${i}`}>
                    <button onClick={() => toggle(s.id, !done)} data-testid={`setup-toggle-${i}`} className="absolute -left-8 top-4 bg-white rounded-full" aria-label="Toggle step">
                      {done ? <CheckCircle size={28} weight="fill" color="var(--bk-success)" /> : <Circle size={28} weight="regular" color="var(--bk-disabled)" />}
                    </button>
                    <div className="bk-card p-5" style={{ borderColor: done ? "var(--bk-success)" : "var(--bk-border)" }}>
                      <div className="flex items-center gap-2 mb-1.5">
                        {s.timeframe && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{s.timeframe}</span>}
                      </div>
                      <h4 className="font-display font-bold text-base" style={{ color: "var(--bk-text)", textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1 }}>{s.title}</h4>
                      {s.description && <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "var(--bk-text-2)" }}>{s.description}</p>}
                      {s.how_to && (
                        <div className="flex gap-2 text-sm leading-relaxed rounded-md p-3 mt-3" style={{ background: "#F6EEDF", color: "var(--bk-text)" }}>
                          <Sparkle size={16} weight="fill" color="#8A6A3B" className="shrink-0 mt-0.5" />
                          <span><span className="font-semibold">How: </span>{s.how_to}</span>
                        </div>
                      )}
                      {s.official_url && (
                        <a href={s.official_url} target="_blank" rel="noopener noreferrer" data-testid={`setup-link-${i}`} className="inline-flex items-center gap-1.5 text-sm font-semibold mt-3 hover:underline" style={{ color: "var(--bk-accent)" }}>
                          Open official site <ArrowSquareOut size={15} weight="bold" />
                        </a>
                      )}
                      {isFormationStep(s, i) && (
                        <div className="mt-4 rounded-2xl border p-4 space-y-3" style={{ borderColor: "rgba(232,168,56,0.28)", background: "#FFF9EF" }} data-testid={`setup-bizee-card-${i}`}>
                          <a
                            href={BIZEE_AFFILIATE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`setup-bizee-link-${i}`}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                            style={{ color: "var(--bk-accent)" }}
                          >
                            Form your LLC with Bizee <ArrowSquareOut size={15} weight="bold" />
                          </a>
                          <p className="text-xs leading-relaxed" style={{ color: "var(--bk-text-2)" }} data-testid={`setup-bizee-disclosure-${i}`}>
                            IronRoot may earn a referral fee if you use this link — at no extra cost to you.
                          </p>
                          <a
                            href={DIY_SECRETARY_OF_STATE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`setup-diy-link-${i}`}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                            style={{ color: "var(--bk-text)" }}
                          >
                            Prefer to do it yourself? Find your state's Secretary of State filing page <ArrowSquareOut size={15} weight="bold" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{plan.starter ? "Starter checklist — general guidance to get moving. Generate above for a state-specific plan. This isn't legal or tax advice." : `AI-generated guidance for ${plan.state}. Verify details on official state/IRS sites; this isn't legal or tax advice.`}</p>
        </>
      )}
    </div>
  );
}
